/**
 * Bands Meter Visualizer - 7-band frequency meter for audio feature debugging
 *
 * Shows the 7 frequency bands (subBass, bass, lowMid, mid, highMid, presence, brilliance)
 * as labeled vertical bars with color gradient from warm to cool.
 */

import { Base2DVisualizer } from '../lib/base-2d-visualizer.js';
import { VisualizerRegistry } from '../visualizer.js';

// Metadata for manifest generation
export const metadata = {
  name: 'bands-meter',
  displayName: 'Bands Meter',
  contextType: '2d',
};

class BandsMeter extends Base2DVisualizer {
  static name = 'bands-meter';
  static displayName = 'Bands Meter';

  /**
   * Get configuration UI definition
   */
  static getConfigUI() {
    return [
      { type: 'range', id: 'smoothing', label: 'Smoothing', min: 0, max: 95, default: 80, scale: 0.01 },
      { type: 'checkbox', id: 'showLabels', label: 'Show Labels', default: true },
      { type: 'checkbox', id: 'showValues', label: 'Show Values', default: false },
      { type: 'checkbox', id: 'glow', label: 'Glow Effect', default: true },
      { type: 'color', id: 'colorStart', label: 'Low Freq Color', default: '#ff4444' },
      { type: 'color', id: 'colorEnd', label: 'High Freq Color', default: '#4444ff' },
      { type: 'color', id: 'backgroundColor', label: 'Background', default: '#0a0a0a' }
    ];
  }

  /**
   * Visualizer-specific default configuration
   */
  getDefaultConfig() {
    return {
      showLabels: true,
      showValues: false,
    };
  }

  /**
   * Initialize state
   */
  init() {
    this.bandNames = ['subBass', 'bass', 'lowMid', 'mid', 'highMid', 'presence', 'brilliance'];
    this.bandLabels = ['Sub', 'Bass', 'LMid', 'Mid', 'HMid', 'Pres', 'Brill'];
    this.smoothedValues = new Float32Array(7);
  }

  /**
   * Returns the number of elements (7 bands)
   */
  getElementCount() {
    return 7;
  }

  /**
   * Interpolate between two hex colors
   */
  interpolateColor(t) {
    const start = this.config.colorStart;
    const end = this.config.colorEnd;

    const r1 = parseInt(start.slice(1, 3), 16);
    const g1 = parseInt(start.slice(3, 5), 16);
    const b1 = parseInt(start.slice(5, 7), 16);

    const r2 = parseInt(end.slice(1, 3), 16);
    const g2 = parseInt(end.slice(3, 5), 16);
    const b2 = parseInt(end.slice(5, 7), 16);

    const r = Math.round(r1 + (r2 - r1) * t);
    const g = Math.round(g1 + (g2 - g1) * t);
    const b = Math.round(b1 + (b2 - b1) * t);

    return `rgb(${r}, ${g}, ${b})`;
  }

  /**
   * Render a single frame
   */
  renderFrame(amplitudes, features) {
    if (!features) return;

    const { ctx, canvas, config, bandNames, bandLabels } = this;
    const { showLabels, showValues } = config;

    // Get band values and apply smoothing
    for (let i = 0; i < 7; i++) {
      const rawValue = features.bands[bandNames[i]] || 0;
      this.smoothedValues[i] = this.smoothedValues[i] * config.smoothing +
        rawValue * (1 - config.smoothing);
    }

    // Calculate dimensions
    const padding = 40;
    const labelHeight = showLabels ? 30 : 0;
    const valueHeight = showValues ? 25 : 0;
    const availableWidth = canvas.width - padding * 2;
    const availableHeight = canvas.height - padding * 2 - labelHeight - valueHeight;
    const barWidth = availableWidth / 7 * 0.7;
    const barGap = availableWidth / 7 * 0.3;

    // Draw bars
    for (let i = 0; i < 7; i++) {
      const value = this.smoothedValues[i];
      const x = padding + i * (barWidth + barGap) + barGap / 2;
      const barHeight = Math.max(2, value * availableHeight);
      const y = padding + valueHeight + availableHeight - barHeight;

      // Get color based on frequency position
      const color = this.interpolateColor(i / 6);

      // Apply glow
      if (config.glow) {
        ctx.shadowBlur = 15 + value * 20;
        ctx.shadowColor = color;
      }

      // Draw bar with rounded top
      ctx.fillStyle = color;
      ctx.beginPath();
      const radius = Math.min(barWidth / 2, 8);
      ctx.roundRect(x, y, barWidth, barHeight, [radius, radius, 0, 0]);
      ctx.fill();

      // Reset glow for text
      ctx.shadowBlur = 0;

      // Draw value above bar
      if (showValues) {
        ctx.fillStyle = '#ffffff';
        ctx.font = '12px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(value.toFixed(2), x + barWidth / 2, y - 8);
      }

      // Draw label below bar
      if (showLabels) {
        ctx.fillStyle = color;
        ctx.font = 'bold 11px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(bandLabels[i], x + barWidth / 2, canvas.height - padding + 15);
      }
    }
  }
}

// Register with the visualizer registry
VisualizerRegistry.register(BandsMeter);

export { BandsMeter };
export default BandsMeter;
